import React, { memo, useMemo, useRef } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvTypography from "@directv-digital/dds.ui.dtv-typography";
import { CheckIcon } from "@directv-digital/fusion.atoms.icons";
import { DropdownListStyles } from "./DropdownList.styles";
import { getChoiceKey, getChoiceLabel, SearchChoiceLike } from "./utils/choice-key";
import { useLocationHref } from "src/hooks/useLocationHref";

{ /* Generated by Copilot- Start - 2026-02-23 16:46:18 */ }
export interface DropdownListProps {
  showDrawer: boolean;
  showDrawerNoMatch: boolean;
  filteredData: any[];
  categoryData: any;
  searchText: string;
  selectedChannels: any[];
  notifyOnSelect?: (channel: SearchChoiceLike) => void;
  notifyOnSelectAll?: (channels: SearchChoiceLike[]) => void;
  notifyOnCloseDropdown?: () => void;
  sortOptions?: boolean;
  isLoading?: boolean;
  /** [WAT-12043] The listbox <ul> id that the parent
   *  ChannelSearch's role="combobox" input references via aria-controls.
   *  Generated per-mount from React.useId() by ChannelSearch so two
   *  concurrent ChannelSearch mounts on the same page (host tools include
   *  ChnlListingPageV2, ChlupInteractiveToolV2 and the agent
   *  PackageCmprGrid) don't collide on a single id. */
  listboxId?: string;
}
/** [WAT-11775] What a row would be announced as, ignoring the tie-breaker — two rows sharing
 *  this string are indistinguishable to a screen reader without one. */
const announceKeyOf = (choice: any): string => `${getChoiceLabel(choice).toLowerCase()}|${choice?.chnlNum ?? ""}`;

/** [WAT-11775] The row's accessible name: label, then the channel number where there is one,
 *  then a position among identically-announced twins where even that is not enough. */
const buildRowAnnouncement = (label: string, chnlNum: any, ordinal: number, twinCount: number): string => {
  const base = chnlNum ? `search - ${label?.toLowerCase?.()} channel ${chnlNum}` : `search - ${label?.toLowerCase?.()}`;
  return ordinal ? `${base}, ${ordinal} of ${twinCount}` : base;
};

const DropdownList = (props: DropdownListProps) => {
  const listRef = useRef<HTMLUListElement | null>(null);
  // [WAT-11775] `selectedChannels` defaulted here rather than guarded at one read. The
  // previous `selectedChannels?.map(...) ?? ''` guard could never be what prevented a crash,
  // because `selectedChannels.some(...)` four lines into the same render dereferenced the
  // same prop unguarded. Defaulting in the destructure is the pattern this file already uses
  // for `sortOptions`/`isLoading` and covers every read.
  const { showDrawer, showDrawerNoMatch, filteredData, categoryData, searchText, selectedChannels = [], sortOptions = true, isLoading = false, listboxId = "channel-search-listbox" } = props;
  const { typingMsg, noMatchListMsg, searchMsg } = categoryData || {};
  const styles = DropdownListStyles({
    showDrawer,
    showDrawerNoMatch
  });
  const url = useLocationHref();
  const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, index: number, channel: any) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      if (typeof channel === 'object') {
        props?.notifyOnSelect?.(channel);
      } else {
        props?.notifyOnSelectAll?.(filteredData);
      }
      return;
    }
    if (event.key === "Escape") {
      props?.notifyOnCloseDropdown();
      return;
    }
    if (!listRef.current) {
      return;
    }
    const items = Array.from(
      listRef.current.querySelectorAll<HTMLLIElement>("li[tabindex='0']")
    );
    if (!items.length) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex = (index + 1) % items.length;
      items[nextIndex]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prevIndex = (index - 1 + items.length) % items.length;
      items[prevIndex]?.focus();
    }
  };
  const selectedChannelsData = useMemo(() => selectedChannels?.map((ch: any) => getChoiceLabel(ch)?.toLowerCase())?.filter(Boolean)?.join(', ') ?? '', [selectedChannels]);
  // [WAT-11775] Built ONCE per render. The per-row `selectedChannels.some(item =>
  // getChoiceKey(item) === channelKey)` rebuilt every selected channel's key for every row:
  // "Select all" on a 2-character query (~60 matches, and neither call site sets
  // maxSelections) is ~3,600 throwaway template strings per keystroke, un-amortised because
  // both call sites pass `channels` as a fresh array literal. Sibling: AccordionRows.tsx.
  // [WAT-11775] Twin counts built ONCE per render, not rescanned per row. The first cut of
  // this filtered `filteredData` inside the row map — ~2n^2 template strings per render, the
  // very allocation pattern the same commit removed from the selected-state check.
  const announceCounts = useMemo(() => {
    const counts = new Map<string, number>();
    (filteredData || []).forEach((c: any) => {
      const key = announceKeyOf(c);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [filteredData]);
  // Running tally, reset each render pass over the rows.
  const announceSeen = new Map<string, number>();
  const selectedChannelKeys = useMemo(
    () => new Set((selectedChannels || []).map((ch: any) => getChoiceKey(ch))),
    [selectedChannels]
  );
  return (
    <DtvBox
      sx={{
        ...styles.root
      }}
    >
      <DtvBox
        sx={{
          ...styles.container
        }}
      >
        {/* [WAT-12043 / YFH-AMP113] Popup role changed from "menu" to
            "listbox" (with option children below) so the pair matches
            the combobox pattern the input in ChannelSearch now
            advertises via aria-controls / aria-expanded. Users can pick
            multiple channels, hence aria-multiselectable. */}
        <ul
          ref={listRef}
          // [WAT-12043 / YFH-AMP113] Prefer the CMS-authored category
          // title ("Channels" / "Shows" from categoryData) so the
          // listbox announcement is localised alongside every other
          // string in this dropdown; falls back to the English literal
          // when categoryData?.title is absent.
          aria-label={categoryData?.title ? `${categoryData.title} suggestions` : "Channel suggestions"}
          aria-multiselectable="true"
          id={listboxId}
          role="listbox"
          style={{
            ...styles.menuUi
          }}
        >
        {/* Loading state — shown while the OV API call is in-flight.
        Suppresses results and no-match rows so the user sees feedback
        instead of a blank or stale dropdown during the request. */}
        {isLoading && !sortOptions && searchText?.length > 1 && (
          <li
            aria-busy="true"
            aria-disabled="true"
            aria-live="polite"
            aria-selected="false"
            key="loadingText"
            role="option"
            tabIndex={-1}
            style={{
              ...styles.menuli
            }}
          >
            <DtvTypography component="span" sx={{
 ...styles.menuP,
fontSize: "16px !important",
fontStyle: "italic"
}}>
              {searchMsg || "Searching…"}
            </DtvTypography>
          </li>
        )}
        {showDrawerNoMatch && (
          // [WAT-12043 / YFH-AMP113] "No match" / "keep typing" is an
          // informational message, not a selectable suggestion —
          // aria-disabled + aria-selected="false" so it is announced by
          // the listbox but never marked as a valid selection target.
          // tabIndex={-1} matches the loading row above — keeping
          // tabIndex={0} on a row announced as disabled let arrow-key
          // traversal (handleKeyDown -> li[tabindex='0']) land on it,
          // which was inconsistent with the file's own pattern.
          <li
            key={'helperText'}
            aria-disabled="true"
            aria-selected="false"
            role="option"
            tabIndex={-1}
            title={searchText?.length === 1 ? typingMsg : noMatchListMsg}
            style={{
              ...styles.menuli
            }}
          >
            <DtvTypography component="span" sx={{
 ...styles.menuP,
fontSize: "16px !important"
}}>
            {searchText?.length === 1 ? typingMsg : noMatchListMsg }
            </DtvTypography>
          </li>
        )}
        {!isLoading && searchText?.length > 1 && (<>
          {filteredData.length > 0 && (<li
            aria-label={`${categoryData?.multiSelectTitle?.toLowerCase?.()}-${searchText?.toLowerCase?.()}`}
            // [WAT-12043 / YFH-AMP113] "Select all matching" is an action
            // trigger, not a persistent selection state — aria-selected
            // stays false regardless of which channels are selected.
            aria-selected="false"
            data-linkdestination={url}
            data-linkposition={"panel_channel lineup"}
            id={`${categoryData?.multiSelectTitle}-${searchText}`}
            key={0}
            role="option"
            tabIndex={0}
            style={{
              ...styles.menuli
            }}
            onClick={() => props?.notifyOnSelectAll?.(filteredData)}
            onKeyDown={event => handleKeyDown(event, 0, 'all')}
          >
            <DtvBox
              component="span"
              sx={{
                ...styles.menuP
              }}
            >
              {categoryData?.multiSelectTitle} <b>{searchText}</b>
            </DtvBox>
          </li>)}
          {filteredData.length > 0 && <DtvTypography component="h6" sx={{
 margin: "10px 10px",
fontWeight: 600
}}>
            {categoryData?.title}
          </DtvTypography>}
          {filteredData.map((channel: any, ix: number) => {
            // [WAT-11767/11775] Match the row against the selection on the shared identity
            // key, so one of two same-named rows can be selected without marking both.
            const channelKey: string = getChoiceKey(channel);
            const isSelected: boolean = selectedChannelKeys.has(channelKey);
            // The DOM id is DERIVED, never the identity key itself. The key contains `|` and
            // `:`, which are not valid in a CSS identifier — `querySelector('#ESPN|206')`
            // throws — and a numberless row produced a trailing-`|` id. The row index keeps it
            // unique even for two rows sharing a name AND a number. Tests that need the
            // identity read `data-choice-key` instead of coupling to this format.
            const rowDomId = `chnl-${ix}-${getChoiceLabel(channel).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
            // [WAT-11775] The channel number is usually what distinguishes two same-named
            // rows — but not always: the live feed carries TWO rows both named "Sports Mix"
            // both on 205, and appending the number leaves those two announced identically.
            // Where even name+number repeats, add the row's position among its twins so a
            // screen-reader user can still tell them apart and getByLabelText stays unique.
            const announceKey = announceKeyOf(channel);
            const twinCount = announceCounts.get(announceKey) || 0;
            const twinOrdinal = twinCount > 1 ? (announceSeen.get(announceKey) || 0) + 1 : 0;
            if (twinCount > 1) {
              announceSeen.set(announceKey, twinOrdinal);
            }
            const displayText: string = getChoiceLabel(channel);
            const searchText: string = props.searchText || "";
            const searchIndex = searchText ?
              displayText.toLowerCase().indexOf(searchText.toLowerCase()) :
              -1;

            const beforeMatch =
              searchIndex >= 0 ? displayText.slice(0, searchIndex) : displayText;
            const matchText =
              searchIndex >= 0 ?
                displayText.slice(searchIndex, searchIndex + searchText.length) :
              "";
            const afterMatch =
              searchIndex >= 0 ?
                displayText.slice(searchIndex + searchText.length) :
              "";

            return (
              <li
                // [WAT-11775] The channel number is what makes two same-named rows
                // DIFFERENT, so it has to be announced. Without it both rows read exactly
                // "search - espn" and a screen-reader user cannot tell which is which — a
                // state WAT-11767 newly made reachable by allowing both to be selected.
                aria-label={buildRowAnnouncement(displayText, channel?.chnlNum, twinOrdinal, twinCount)}
                // eslint-disable-next-line jsx-a11y/aria-props
                aria-linklabel={`search - ${selectedChannelsData}`}
                aria-selected={isSelected}
                data-linkdestination={url}
                data-linkposition={"panel_channel lineup"}
                data-choice-key={channelKey}
                            id={rowDomId}
                key={`${ix + 1}`}
                role="option"
                // [WAT-12043 / YFH-AMP113] Each option keeps `tabIndex={0}`
                // as a KNOWN deviation from the strict WAI-ARIA 1.2
                // combobox-with-listbox pattern (which puts the input at
                // tabIndex=0, options at tabIndex=-1, and tracks the
                // highlighted option via aria-activedescendant on the
                // input). Existing arrow-key traversal via handleKeyDown
                // already moves DOM focus between options; migrating to
                // roving tabindex + aria-activedescendant is a follow-up
                // ticket, tracked separately from this PR.
                tabIndex={0}
                title={isSelected ? `${displayText} (is selected)` : displayText}
                style={{
                  ...styles.menuli,
                  ...isSelected && {
                    backgroundColor: "#1026411f"
                  }
                }}
                onClick={() => props?.notifyOnSelect?.(channel)}
                onKeyDown={event => handleKeyDown(event, ix + 1, channel)}
              >
                <DtvBox
                  component={"div"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%"
                  }}
                >
                  {isSelected && <CheckIcon fill="#121212" style={{ height: "20px" }} />}
                  <DtvBox
                    component="span"
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "row",
                      gap: "2px"
                    }}
                  >
                    <DtvBox
                      component="span"
                      sx={{
                        ...styles.menuP,
                        color: isSelected ? "#121212 !important" : "#102641 !important"
                      }}
                    >
                      {beforeMatch}
                      {matchText && (
                        <span style={{ fontWeight: 600 }}>{matchText}</span>
                      )}
                      {afterMatch}
                    </DtvBox>
                    {(!sortOptions && (channel?.category || channel?.genre)) && (
                      <DtvTypography
                        component="span"
                        sx={{
                          ...styles.menuP,
                          fontSize: "12px !important",
                          color: "#888 !important",
                          fontWeight: 400
                        }}
                      >
                         - {channel?.category && channel?.genre ?
                          `${channel.category} | ${channel.genre}` :
                          channel?.category || channel?.genre
                        }
                      </DtvTypography>
                    )}
                  </DtvBox>
                </DtvBox>
              </li>
            );
          })}</>)}
        </ul>
      </DtvBox>
    </DtvBox>
  );
};

export const MemoizedDropdownList = memo(DropdownList);
{ /* Generated by Copilot- End - 2026-02-23 16:46:18 */ }
